import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@gs/auth';
import { api } from '../services/api';
import { useTranslation } from '../i18n';

type Health = 'green' | 'yellow' | 'red';

interface AppRow {
  id: string;
  appKey: string;
  appName: string;
  prefix: string;
  icon: string;
  status: string;
  inbound: {
    lastRunAt: string | null;
    lastRunStatus: string | null;
    lastRunCount: number | null;
    hoursSince: number | null;
    health: Health;
  };
  outbound: {
    lastRunAt: string | null;
    lastRunStatus: string | null;
    lastRunCount: number | null;
    hoursSince: number | null;
    health: Health;
  };
}

function HealthDot({ health }: { health: Health }) {
  const cls =
    health === 'green'
      ? 'bg-emerald-500'
      : health === 'yellow'
        ? 'bg-amber-400'
        : 'bg-red-500';
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${cls}`} title={health} />;
}

function SyncLine({
  label,
  side,
}: {
  label: string;
  side: AppRow['inbound'];
}) {
  const ago =
    side.hoursSince === null
      ? '—'
      : side.hoursSince === 0
        ? '< 1 h'
        : `${side.hoursSince} h`;
  return (
    <div className="flex items-start justify-between gap-2 text-sm">
      <div>
        <span className="text-slate-500">{label}</span>
        <div className="text-slate-700 mt-0.5">
          Dernier : {side.lastRunAt ? new Date(side.lastRunAt).toLocaleString('fr-CA') : 'jamais'}
        </div>
        <div className="text-xs text-slate-500">
          Il y a {ago} · {side.lastRunCount != null ? `${side.lastRunCount} enreg.` : '—'}
        </div>
      </div>
      <HealthDot health={side.health} />
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'apps'],
    queryFn: () => api.get<AppRow[]>('/api/apps'),
    enabled: !!user,
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-slate-200 rounded-sm w-1/3" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-48 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800">
        <p className="font-medium">Impossible de charger les applications.</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 text-sm underline"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const apps = data ?? [];

  return (
    <div className="space-y-6">
      <div>
        {/* Ce tableau montre la FRAICHEUR DES SYNCHRONISATIONS, pas la sante des apps — celle-ci
            vit dans « Sante du parc » (arbitrage rendu, David, 2026-09-24 20 h 15). */}
        <h2 className="text-xl font-semibold text-slate-800">{t('admin:dashboard.syncFreshnessTitle')}</h2>
        <p className="text-sm text-slate-600 mt-1">
          Badges : vert (&lt; 25 h), jaune (25–48 h), rouge (&gt; 48 h ou erreur)
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {apps.map((a) => (
          <article
            key={a.id}
            className="bg-white rounded-xl border border-slate-200 shadow-xs p-4 flex flex-col gap-4"
          >
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <span className="text-2xl" aria-hidden>
                {a.icon}
              </span>
              <div>
                <h3 className="font-semibold text-slate-800">{a.appName}</h3>
                <p className="text-xs text-slate-500">
                  {a.appKey} · {a.prefix}{' '}
                  <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600">
                    {a.status}
                  </span>
                </p>
              </div>
            </div>
            <SyncLine label="Inbound (SOFI → Azure)" side={a.inbound} />
            <SyncLine label="Outbound (Azure → SOFI)" side={a.outbound} />
          </article>
        ))}
      </div>
    </div>
  );
}
